import React, { useState, useContext, forwardRef } from 'react';
import { DocumentsContext } from '../../contexts/DocumentsContext';
import FileUploadArea from '../ui/FileUploadArea';
import StatusModal from '../ui/StatusModal';
import DocumentsTable from '../profile/DocumentsTable';
import { FileTextIcon } from '../common/IconsComponent';
import DocumentTypeSelector from './DocumentTypeSelector';
import SpinnerOverlay from '../ui/SpinnerOverlay';
import { useUser } from '../../contexts/UserContext';

const DocumentsSection = forwardRef(({ active }, ref) => {
  const {
    documents,
    availableTypes,
    addDocument,
    hideDocument,
    viewFile,
    viewedFileUrl,
    closeFileView,
    viewStatus,
    selectedDocForStatus,
    closeStatus,
    isLoading,
  } = useContext(DocumentsContext);

  const { user, userType, showAlert } = useUser();

  const effectiveRole = user?.role || userType || 'student';
  const isSupplier = effectiveRole === 'supplier';

  const [selectedDocType, setSelectedDocType] = useState('');
  const [customDocName, setCustomDocName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  const resetFile = () => {
    setSelectedFile(null);
    setCustomDocName('');
    setFileInputKey(Date.now());
  };

  const handleFileSelection = (file) => {
    if (file?.type === 'application/pdf') {
      setSelectedFile(file);
      setCustomDocName(file.name.replace(/\.pdf$/i, ''));
    } else {
      showAlert('אנא בחר קובץ PDF בלבד', 'error');
      resetFile();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !customDocName.trim() || !selectedDocType) {
      showAlert('נא לבחור סוג מסמך, קובץ ולהזין שם', 'error');
      return;
    }

    try {
      await addDocument(
        selectedFile,
        selectedDocType,
        customDocName.trim()
      );

      resetFile();
      setSelectedDocType('');

      showAlert('המסמך הועלה בהצלחה', 'success');
    } catch (err) {
      showAlert(
        err?.message || 'שגיאה בהעלאת המסמך',
        'error'
      );
    }
  };

  const handleHideDocument = async (docId) => {
    const doc = documents.find((d) => d.id === docId);

    if (!doc) return;

    try {
      await hideDocument(doc);

      showAlert(
        'המסמך הוסר בהצלחה',
        'success'
      );
    } catch (err) {
      showAlert(
        err?.message || 'שגיאה בהסרת המסמך',
        'error'
      );
    }
  };

  if (!active) return null;

  return (
    <div
      dir="rtl"
      ref={ref}
      id="documents-section"
      className="scroll-mt-24"
    >
      <div className="relative bg-white rounded-2xl shadow-md border border-gray-100 p-4 sm:p-6 mb-4 sm:mb-6">

        {isLoading && (
          <SpinnerOverlay
            variant="glow"
            text="נא להמתין"
            dark={false}
            blur={false}
          />
        )}

        <div className="flex items-center gap-2 mb-4 sm:mb-5 pb-4 border-b border-gray-100">
          <FileTextIcon
            size={20}
            className="text-indigo-500 flex-shrink-0"
          />

          <h2 className="text-lg sm:text-xl font-bold text-gray-800">
            מסמכים
          </h2>
        </div>

        {isSupplier && (
          <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3">
            <p className="text-sm font-semibold text-indigo-800 mb-1">
              מסמכים נדרשים לאישור ספק
            </p>

            <p className="text-sm text-indigo-700 leading-6">
              יש להעלות אישור ניהול חשבון, תעודת עוסק מורשה ואישור ניכוי מס במקור.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3 sm:gap-4 mb-5 sm:mb-6">
          <DocumentTypeSelector
            selectedDocType={selectedDocType}
            setSelectedDocType={setSelectedDocType}
            documentTypes={availableTypes}
          />

          <FileUploadArea
            selectedFile={selectedFile}
            fileInputKey={fileInputKey}
            onFileChange={handleFileSelection}
            onRemoveFile={resetFile}
            onUpload={handleUpload}
          />
        </div>

        <DocumentsTable
          documents={documents}
          onDelete={handleHideDocument}
          onViewStatus={viewStatus}
          onViewFile={(doc) => viewFile(doc.url)}
        />
      </div>

      {selectedDocForStatus && (
        <StatusModal
          document={selectedDocForStatus}
          onClose={closeStatus}
        />
      )}

      {viewedFileUrl && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col w-full max-w-4xl h-[80vh]">

            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-700">
                צפייה במסמך
              </span>

              <button
                type="button"
                onClick={closeFileView}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
                aria-label="סגור"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <iframe
              src={viewedFileUrl}
              className="flex-1 w-full"
              title="מסמך"
            />
          </div>
        </div>
      )}
    </div>
  );
});

DocumentsSection.displayName = 'DocumentsSection';

export default DocumentsSection;